import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";

import { useLocation } from "react-router";

import { useAuth } from "../../services/backend/AuthProvider.js";
import { useBackend } from "../../services/backend/BackendProvider.js";
import { loadContentAdminStatus } from "./adminContentRepository.js";

type AdminAccessStatus =
    | "unchecked"
    | "checking"
    | "allowed"
    | "denied"
    | "error";

interface AdminAccessValue {
    status: AdminAccessStatus;
    requestAccess: () => void;
    retry: () => void;
}

interface AdminAccessResult {
    userId: string;
    status: Exclude<AdminAccessStatus, "unchecked">;
}

const AdminAccessContext = createContext<AdminAccessValue | null>(null);

function AdminAccessProvider({ children }: { children: ReactNode }) {
    const { status: authStatus, user } = useAuth();
    const { client } = useBackend();
    const location = useLocation();
    const [requested, setRequested] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [result, setResult] = useState<AdminAccessResult | null>(null);

    const requestAccess = useCallback(() => setRequested(true), []);
    const retry = useCallback(() => setRetryCount(value => value + 1), []);
    const shouldCheck = requested || location.pathname === "/admin/content";
    const userId = user?.id ?? null;

    useEffect(() => {
        if (!shouldCheck || authStatus !== "signed-in" || !userId || !client) {
            return;
        }

        let active = true;
        setResult({ userId, status: "checking" });

        void loadContentAdminStatus(client).then(
            allowed => {
                if (active) {
                    setResult({ userId, status: allowed ? "allowed" : "denied" });
                }
            },
            () => {
                if (active) setResult({ userId, status: "error" });
            }
        );

        return () => { active = false; };
    }, [authStatus, client, retryCount, shouldCheck, userId]);

    const status: AdminAccessStatus =
        authStatus === "signed-in" && client && userId && result?.userId === userId
            ? result.status
            : "unchecked";

    const value = useMemo<AdminAccessValue>(
        () => ({ status, requestAccess, retry }),
        [status, requestAccess, retry]
    );

    return (
        <AdminAccessContext.Provider value={value}>
            {children}
        </AdminAccessContext.Provider>
    );
}

function useAdminAccess(): AdminAccessValue {
    const value = useContext(AdminAccessContext);
    if (!value) throw new Error("useAdminAccess must be used within AdminAccessProvider");
    return value;
}

export { AdminAccessProvider, useAdminAccess };
export type { AdminAccessValue };
